import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { emitUpgradePrompt } from "@/lib/upgradePrompt";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Send, Sparkles, Plus } from "lucide-react";
import { trackOnboarding } from "@/lib/onboardingTrack";

const AVATAR = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGZyaWVuZGx5fGVufDB8fHx8MTc4MjkxMjg4NHww&ixlib=rb-4.1.0&q=85&w=200";

export default function Advisor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  const SUGGESTIONS = [
    t("pages.advisor.sugg_debt"),
    t("pages.advisor.sugg_savings"),
    t("pages.advisor.sugg_subs"),
    t("pages.advisor.sugg_emergency"),
    t("pages.advisor.sugg_health"),
  ];

  const loadSessions = async () => {
    const { data } = await api.get("/advisor/sessions");
    setSessions(data);
  };
  const loadHistory = async (sid) => {
    if (!sid) { setMessages([]); return; }
    const { data } = await api.get(`/advisor/history?session_id=${sid}`);
    setMessages(data);
  };

  useEffect(() => { trackOnboarding("first_view_advisor"); loadSessions(); }, []);
  useEffect(() => { loadHistory(sessionId); }, [sessionId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg }]);
    setStreaming(true);
    setMessages(m => [...m, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      // Free-tier gate — surface the Upgrade modal instead of showing a
      // stream error. The axios interceptor doesn't apply here because
      // SSE requires raw fetch().
      if (res.status === 402) {
        let detail = null;
        try {
          const body = await res.json();
          detail = body?.detail || body;
        } catch (jsonErr) {
          console.warn("Advisor 402 detail parse failed", jsonErr);
        }
        if (detail) emitUpgradePrompt(detail);
        setMessages(m => m.slice(0, -1));   // remove the empty assistant bubble
        return;
      }
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let currentSid = sessionId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const lines = p.split("\n");
          const evLine = lines.find(l => l.startsWith("event:"));
          const dataLines = lines.filter(l => l.startsWith("data:")).map(l => l.slice(5).replace(/^ /, ""));
          const data = dataLines.join("\n");
          if (evLine?.includes("done")) {
            currentSid = data.trim();
            if (!sessionId) setSessionId(currentSid);
          } else if (data) {
            setMessages(m => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: (copy[copy.length - 1].content || "") + data };
              return copy;
            });
          }
        }
      }
      loadSessions();
    } catch (e) {
      console.error(e);
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("pages.advisor.reply_error") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const newSession = () => { setSessionId(null); setMessages([]); };

  return (
    <div>
      <PageHeader subtitle={t("pages.advisor.subtitle")} title={t("pages.advisor.title")}
        action={<button className="btn-primary" onClick={newSession} data-testid="new-chat-btn"><Plus size={16}/> {t("pages.advisor.new_chat")}</button>}/>

      <div className="grid lg:grid-cols-4 gap-5">
        {/* Session list */}
        <aside className="card-flat p-4 h-fit hidden lg:block">
          <div className="label-eyebrow mb-3">{t("pages.advisor.conversations")}</div>
          {sessions.length === 0 ? (
            <div className="text-xs text-[var(--text-muted)]">{t("pages.advisor.start_hint")}</div>
          ) : (
            <div className="space-y-1">
              {sessions.map(s => (
                <button key={s.session_id} onClick={() => setSessionId(s.session_id)}
                  className={`w-full text-left p-2.5 rounded-lg text-sm hover:bg-[var(--bg-accent)] transition ${sessionId===s.session_id?"bg-[var(--bg-accent)]":""}`}
                  data-testid={`session-${s.session_id}`}>
                  <div className="line-clamp-2 text-[var(--text-primary)]">{s.last_message || t("pages.advisor.session_new")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mt-1">{t("pages.advisor.session_msgs", { count: s.count })}</div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Chat area */}
        <div className="lg:col-span-3 card-flat overflow-hidden flex flex-col" style={{ minHeight: "70vh" }}>
          {/* Header */}
          <div className="border-b border-[var(--border-default)] px-5 py-4 flex items-center gap-3 bg-[var(--bg-secondary)]/40">
            <img src={AVATAR} className="h-10 w-10 rounded-full object-cover border border-[var(--border-default)]" alt=""/>
            <div>
              <div className="font-medium">{t("pages.advisor.avatar_name")}</div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Sparkles size={11}/> {t("pages.advisor.powered_by")}</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-5 overflow-y-auto flex flex-col gap-3">
            {messages.length === 0 && !streaming && (
              <div className="my-6 text-center animate-fadeup">
                <img src={AVATAR} className="h-20 w-20 rounded-full mx-auto object-cover border border-[var(--border-default)]" alt=""/>
                <div className="mt-4 text-lg" style={{fontFamily:'var(--font-heading)'}}>
                  {user?.name ? t("pages.advisor.hi_name", { name: user.name.split(" ")[0] }) : t("pages.advisor.hi_default")}
                </div>
                <div className="text-sm text-[var(--text-secondary)] mt-1 max-w-md mx-auto">{t("pages.advisor.hi_body")}</div>
                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)} className="text-xs px-3 py-2 rounded-full border border-[var(--border-default)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition"
                      data-testid={`suggestion-${s.slice(0,15)}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => (
              <div key={`${idx}-${m.role}`} className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}>
                {m.content || (streaming && idx === messages.length - 1 ? "…" : "")}
              </div>
            ))}
            <div ref={bottomRef}/>
          </div>

          {/* Input */}
          <form onSubmit={e => { e.preventDefault(); send(); }} className="border-t border-[var(--border-default)] p-4 bg-[var(--bg-primary)]">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t("pages.advisor.input_p")}
                className="field-input resize-none flex-1"
                disabled={streaming}
                data-testid="chat-input"
              />
              <button type="submit" className="btn-primary" disabled={streaming || !input.trim()} data-testid="chat-send-btn">
                <Send size={16}/>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
